import { supabase } from '@/lib/supabase';

export const DAILY_LOG_SYNC_EVENT = '__jphph_daily_logs_sync__';

export interface DailyLogEntryRecord {
  id: string;
  project_id: string;
  log_date: string;
  title: string | null;
  management_comment: string | null;
  internal_note: string | null;
  visibility: string;
  is_published_to_client: boolean;
  published_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface DailyLogPhotoRecord {
  id: string;
  daily_log_id: string;
  photo_url: string;
  caption: string | null;
  source_photo_id?: string | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
}

export function emitDailyLogSync(projectId?: string): void {
  window.dispatchEvent(
    new CustomEvent(DAILY_LOG_SYNC_EVENT, { detail: { projectId } })
  );
}

/** PM Management Daily Log template field labels. */
export const PM_DAILY_LOG_TEMPLATE_FIELDS = [
  'Main work / Civil work',
  'Other work completed',
  'Delivery / Materials',
  'Subcontractors onsite',
  'Staff onsite',
  'Weather',
  'Safety / Incidents',
  'Delays / Issues',
  'Tomorrow / Next steps',
] as const;

/** Subcontractor Daily Log template field labels. */
export const SUBCONTRACTOR_DAILY_LOG_TEMPLATE_FIELDS = [
  'Main work / Civil work',
  'Weather check',
  'Material delivery',
  'Any JPH on site control or work or Daily Check',
  'Any JPH QS or quote work?',
  'Site safe - Any incident report / N/A / possible Health and Safety issues',
  'Sediment control',
  'Any neighbour issue',
  'EOT',
] as const;

export const SUBCONTRACTOR_DAILY_LOG_SOURCE_MARKER = '__jph_subcontractor_daily_log__';

export function formatDailyLogAutoTitle(logDate: string): string {
  if (!logDate || !/^\d{4}-\d{2}-\d{2}$/.test(logDate)) return 'Daily Log';
  const d = new Date(`${logDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return 'Daily Log';
  const formatted = d.toLocaleDateString('en-NZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return `Daily Log - ${formatted}`;
}

export function isAutoGeneratedDailyLogTitle(title: string, logDate: string): boolean {
  const trimmed = title.trim();
  if (!trimmed) return true;
  return trimmed === formatDailyLogAutoTitle(logDate);
}

export function buildDailyLogTemplateComment(
  fieldLabels: readonly string[],
  values: string[]
): string {
  return fieldLabels
    .map((label, index) => `${index + 1}. ${label}: ${values[index]?.trim() || '-'}`)
    .join('\n');
}

export function parseDailyLogTemplateComment(
  fieldLabels: readonly string[],
  value: string
): string[] | null {
  if (!value.trim()) return null;
  const lines = value.split(/\r?\n/);
  if (lines.length !== fieldLabels.length) return null;

  const parsed = fieldLabels.map((label, index) => {
    const match = lines[index].match(
      new RegExp(`^${index + 1}\\.\\s+${label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}:\\s*(.*)$`)
    );
    return match ? (match[1] === '-' ? '' : match[1]) : null;
  });

  return parsed.every((item) => item !== null) ? (parsed as string[]) : null;
}

export function isSubcontractorDailyLogEntry(internalNote: string | null | undefined): boolean {
  return internalNote === SUBCONTRACTOR_DAILY_LOG_SOURCE_MARKER;
}

/** Load subcontractor daily log rows for calendar/day views (same table as PM Daily Log tab). */
export async function loadSubcontractorDailyLogs(
  projectIds: string[],
  userId: string
): Promise<{ logs: DailyLogEntryRecord[]; error?: string }> {
  if (projectIds.length === 0 || !userId) {
    return { logs: [] };
  }

  const { data, error } = await supabase
    .from('jphph_daily_logs')
    .select(
      'id, project_id, log_date, title, management_comment, internal_note, visibility, is_published_to_client, published_at, created_by, created_at, updated_at'
    )
    .in('project_id', projectIds)
    .eq('created_by', userId)
    .eq('internal_note', SUBCONTRACTOR_DAILY_LOG_SOURCE_MARKER)
    .neq('visibility', 'archived')
    .order('log_date', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) {
    return { logs: [], error: error.message };
  }

  return { logs: (data || []) as DailyLogEntryRecord[] };
}

/** Create or update a subcontractor daily log record before attaching photos. */
export async function upsertSubcontractorDailyLog(params: {
  projectId: string;
  logDate: string;
  userId: string;
  title: string;
  managementComment: string;
}): Promise<{ dailyLogId: string | null; error?: string }> {
  const { projectId, logDate, userId, title, managementComment } = params;

  const { data: existing } = await supabase
    .from('jphph_daily_logs')
    .select('id')
    .eq('project_id', projectId)
    .eq('log_date', logDate)
    .eq('created_by', userId)
    .eq('internal_note', SUBCONTRACTOR_DAILY_LOG_SOURCE_MARKER)
    .maybeSingle();

  const payload = {
    project_id: projectId,
    log_date: logDate,
    title: title.trim() || formatDailyLogAutoTitle(logDate),
    management_comment: managementComment.trim() || null,
    internal_note: SUBCONTRACTOR_DAILY_LOG_SOURCE_MARKER,
    visibility: 'internal' as const,
    is_published_to_client: false,
    published_at: null,
    created_by: userId,
    updated_at: new Date().toISOString(),
  };

  if (existing?.id) {
    const { data, error } = await supabase
      .from('jphph_daily_logs')
      .update(payload)
      .eq('id', existing.id)
      .select('id')
      .maybeSingle();
    if (error) return { dailyLogId: null, error: error.message };
    emitDailyLogSync(projectId);
    return { dailyLogId: data?.id ?? existing.id };
  }

  const { data, error } = await supabase
    .from('jphph_daily_logs')
    .insert(payload)
    .select('id')
    .maybeSingle();
  if (error) return { dailyLogId: null, error: error.message };
  emitDailyLogSync(projectId);
  return { dailyLogId: data?.id ?? null };
}

/** Link an uploaded project file to a daily log (photos + files junction tables). */
export async function linkProjectFileToDailyLog(params: {
  dailyLogId: string;
  projectId: string;
  fileId: string;
  photoUrl: string;
  caption?: string | null;
  uploadedBy: string;
}): Promise<{ error?: string }> {
  const { dailyLogId, projectId, fileId, photoUrl, caption, uploadedBy } = params;

  const { error: photoErr } = await supabase.from('jphph_daily_log_photos').insert({
    daily_log_id: dailyLogId,
    project_id: projectId,
    source_photo_id: fileId,
    photo_url: photoUrl,
    caption: caption?.trim() || null,
    uploaded_by: uploadedBy,
  });
  if (photoErr) return { error: photoErr.message };

  const { error: fileErr } = await supabase.from('jphph_daily_log_files').insert({
    daily_log_id: dailyLogId,
    project_id: projectId,
    file_id: fileId,
  });
  if (fileErr) return { error: fileErr.message };

  await supabase
    .from('jphph_project_files')
    .update({ daily_log_id: dailyLogId })
    .eq('id', fileId);

  emitDailyLogSync(projectId);
  return {};
}

export async function archiveDailyLog(
  id: string,
  projectId: string
): Promise<{ error?: string }> {
  const { error } = await supabase
    .from('jphph_daily_logs')
    .update({
      visibility: 'archived',
      is_published_to_client: false,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id);
  if (error) return { error: error.message };
  emitDailyLogSync(projectId);
  return {};
}

export async function deleteDailyLog(
  id: string,
  projectId: string
): Promise<{ error?: string }> {
  await supabase.from('jphph_daily_log_photos').delete().eq('daily_log_id', id);
  await supabase.from('jphph_daily_log_files').delete().eq('daily_log_id', id);

  const { error } = await supabase.from('jphph_daily_logs').delete().eq('id', id);
  if (error) return { error: error.message };
  emitDailyLogSync(projectId);
  return {};
}

/** Remove a photo link from a daily log without deleting the underlying file. */
export async function unlinkDailyLogPhoto(
  photoLinkId: string,
  sourcePhotoId?: string | null
): Promise<void> {
  const { data: row } = await supabase
    .from('jphph_daily_log_photos')
    .select('daily_log_id, source_photo_id')
    .eq('id', photoLinkId)
    .maybeSingle();

  await supabase.from('jphph_daily_log_photos').delete().eq('id', photoLinkId);

  const fileId = sourcePhotoId ?? (row as { source_photo_id?: string | null } | null)?.source_photo_id;
  const dailyLogId = (row as { daily_log_id?: string } | null)?.daily_log_id;
  if (fileId && dailyLogId) {
    await supabase
      .from('jphph_daily_log_files')
      .delete()
      .eq('daily_log_id', dailyLogId)
      .eq('file_id', fileId);
  }
}

export async function loadDailyLogPhotos(
  logIds: string[]
): Promise<Record<string, DailyLogPhotoRecord[]>> {
  if (logIds.length === 0) return {};

  const { data: photoRows } = await supabase
    .from('jphph_daily_log_photos')
    .select('id, daily_log_id, photo_url, caption, source_photo_id')
    .in('daily_log_id', logIds)
    .order('created_at', { ascending: true });

  const rawPhotos = photoRows || [];

  // Fetch project files for ALL source_photo_ids (signed URL support + fallback URL)
  const allSourceIds = rawPhotos
    .filter((p: { source_photo_id: string | null }) => p.source_photo_id)
    .map((p: { source_photo_id: string }) => p.source_photo_id);

  type PfRow = { id: string; file_url: string; storage_bucket: string | null; storage_path: string | null };
  const pfMap = new Map<string, PfRow>();
  if (allSourceIds.length > 0) {
    const { data: pfRows } = await supabase
      .from('jphph_project_files')
      .select('id, file_url, storage_bucket, storage_path')
      .in('id', allSourceIds)
      .eq('is_deleted', false);
    ((pfRows || []) as PfRow[]).forEach((pf) => {
      pfMap.set(pf.id, pf);
    });
  }

  const map: Record<string, DailyLogPhotoRecord[]> = {};
  for (const p of rawPhotos as {
    id: string;
    daily_log_id: string;
    photo_url: string | null;
    source_photo_id: string | null;
    caption: string | null;
  }[]) {
    let url = p.photo_url || '';
    let storageBucket: string | null = null;
    let storagePath: string | null = null;
    if (p.source_photo_id) {
      const pf = pfMap.get(p.source_photo_id);
      if (pf) {
        if (!url) url = pf.file_url || '';
        storageBucket = pf.storage_bucket;
        storagePath = pf.storage_path;
      }
    }
    if (!url) continue;
    if (!map[p.daily_log_id]) map[p.daily_log_id] = [];
    map[p.daily_log_id].push({
      id: p.id,
      daily_log_id: p.daily_log_id,
      photo_url: url,
      caption: p.caption,
      source_photo_id: p.source_photo_id,
      storage_bucket: storageBucket,
      storage_path: storagePath,
    });
  }
  return map;
}
